import type { KeyboardEvent, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, BookOpen, Clock, Sparkles } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * OllieLearnsPromoBanner — the "Meet Ollie Learns" discovery banner that
 * lives between the AI hero card and the personalized shelf on the
 * Library page.
 *
 * Built strictly to the provided spec. Tokens are expressed as the exact
 * hex values from the doc because the banner deliberately introduces a
 * *new* accent (teal #2E8A8A / #1F6A6A) that is not part of the Library's
 * `or-*` palette — pinning the hex here keeps the banner visually
 * distinct without polluting the theme.
 *
 * Behaviour:
 *   - The entire banner is clickable and routes to `/learns` (the Ollie
 *     Learns landing page; route will land in a follow-up change).
 *   - The CTA button is a real `<button>` and stops propagation so
 *     clicks don't double-fire the parent card.
 *   - Keyboard users can activate the card with Enter / Space (role="link"
 *     + tabIndex={0}).
 *   - The decorative radial glow is `aria-hidden`.
 */
export interface OllieLearnsPromoBannerProps {
  /** Override the default banner target. Defaults to `/learns`. */
  to?: string;
  className?: string;
}

export function OllieLearnsPromoBanner({ to = '/learns', className }: OllieLearnsPromoBannerProps) {
  const navigate = useNavigate();

  const go = () => navigate(to);

  const handleCardClick = () => go();
  const handleCardKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      go();
    }
  };
  const handleCtaClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    go();
  };

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={handleCardKey}
      aria-label="Meet Ollie Learns — articles that teach & surprise"
      className={cn(
        'group relative grid cursor-pointer items-center overflow-hidden',
        'transition-[transform,box-shadow] duration-200 ease-out',
        'hover:-translate-y-[2px] hover:shadow-[0_20px_50px_rgba(31,27,22,0.08),0_4px_12px_rgba(31,27,22,0.04)]',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2E8A8A]/40 focus-visible:ring-offset-2',
        className,
      )}
      style={{
        gridTemplateColumns: '1fr auto',
        columnGap: '24px',
        padding: '24px 28px',
        borderRadius: '22px',
        border: '1px solid #E3D5B8',
        backgroundImage: 'linear-gradient(100deg, #F6F0E3 0%, #EDF2EE 50%, #E3EEEB 100%)',
      }}
    >
      {/* --- Decorative accents (all aria-hidden) --- */}
      <div
        aria-hidden
        className="pointer-events-none absolute"
        style={{
          right: '-30px',
          top: '-30px',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(46, 138, 138, 0.10), transparent 70%)',
        }}
      />

      {/* --- Left column: content --- */}
      <div className="relative flex flex-col">
        {/* Eyebrow row */}
        <div className="inline-flex items-center" style={{ gap: '8px' }}>
          <span
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: '#1F6A6A',
            }}
          >
            New — Just for Curious Kids
          </span>
          <span
            style={{
              backgroundColor: '#2E8A8A',
              color: '#FFFFFF',
              fontFamily: 'Inter, sans-serif',
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.08em',
              padding: '2px 8px',
              borderRadius: '999px',
              lineHeight: 1.4,
            }}
          >
            BETA
          </span>
        </div>

        {/* Headline */}
        <h2
          style={{
            fontFamily: '"DM Serif Display", serif',
            fontWeight: 400,
            fontSize: '28px',
            lineHeight: 1.1,
            letterSpacing: '-0.01em',
            color: '#1F1B16',
            margin: '8px 0',
          }}
        >
          Meet <span style={{ color: '#1F6A6A' }}>Ollie Learns</span> — articles that teach &amp;
          surprise
        </h2>

        {/* Description */}
        <p
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '14px',
            lineHeight: 1.5,
            color: '#3D342A',
            maxWidth: '520px',
            margin: '0 0 16px 0',
          }}
        >
          Short, illustrated articles on history and today&apos;s world — written for grades 3–8.
          Tap tricky words to see what they mean, then quiz yourself to earn coins.
        </p>

        {/* CTA row */}
        <div className="flex flex-wrap items-center" style={{ gap: '8px' }}>
          <button
            type="button"
            onClick={handleCtaClick}
            className="inline-flex items-center transition-colors"
            style={{
              backgroundColor: '#2E8A8A',
              color: '#FFFFFF',
              padding: '10px 18px',
              borderRadius: '999px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontWeight: 600,
              gap: '8px',
              boxShadow: '0 1px 2px rgba(31, 27, 22, 0.04)',
              border: 'none',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#1F6A6A';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#2E8A8A';
            }}
            onFocus={(e) => {
              e.currentTarget.style.backgroundColor = '#1F6A6A';
            }}
            onBlur={(e) => {
              e.currentTarget.style.backgroundColor = '#2E8A8A';
            }}
          >
            Explore Ollie Learns
            <ArrowRight
              style={{ width: '14px', height: '14px', color: 'currentColor' }}
              strokeWidth={2}
              aria-hidden
            />
          </button>

          {/* Meta strip */}
          <div
            className="inline-flex items-center flex-wrap"
            style={{ gap: '12px', marginLeft: '8px' }}
          >
            <MetaItem icon={<Sparkles strokeWidth={2} style={metaIconStyle} aria-hidden />}>
              Quizzes &amp; coins
            </MetaItem>
            <MetaItem icon={<Clock strokeWidth={2} style={metaIconStyle} aria-hidden />}>
              5–10 min reads
            </MetaItem>
          </div>
        </div>
      </div>

      {/* --- Right column: mini article preview stack --- */}
      <div className="relative" aria-hidden style={{ width: '240px', height: '150px' }}>
        {/* Card A — Ancient Rome (back of stack, warm tones, -4°) */}
        <div
          className="absolute"
          style={{
            right: '40px',
            top: 0,
            transform: 'rotate(-4deg)',
          }}
        >
          <MiniArticleCard eyebrow="HISTORY" title="Ancient Rome" toneA="#F6E2C4" toneB="#ECD1A8" />
        </div>
        {/* Card B — How Volcanoes Work (front of stack, teal tones, +5°) */}
        <div
          className="absolute"
          style={{
            right: 0,
            top: '20px',
            transform: 'rotate(5deg)',
          }}
        >
          <MiniArticleCard
            eyebrow="SCIENCE"
            title="How Volcanoes Work"
            toneA="#D4E8E3"
            toneB="#BFD9D3"
          />
        </div>
      </div>
    </div>
  );
}

// -- Helpers --------------------------------------------------------------

const metaIconStyle: React.CSSProperties = {
  width: '12px',
  height: '12px',
  color: '#8A7F72',
  flexShrink: 0,
};

function MetaItem({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <span
      className="inline-flex items-center"
      style={{
        gap: '6px',
        fontFamily: 'Inter, sans-serif',
        fontSize: '12px',
        color: '#8A7F72',
      }}
    >
      {icon}
      {children}
    </span>
  );
}

interface MiniArticleCardProps {
  eyebrow: string;
  title: string;
  toneA: string;
  toneB: string;
}

/**
 * Mini article card used in the banner's preview stack. Fixed 150px wide;
 * the thumbnail area uses `aspect-ratio: 4 / 3` so height derives from
 * width (≈112px) and stays in sync if the stack container ever changes.
 */
function MiniArticleCard({ eyebrow, title, toneA, toneB }: MiniArticleCardProps) {
  return (
    <div
      style={{
        width: '150px',
        backgroundColor: '#FFFFFF',
        border: '1px solid #EFE5D2',
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 6px 20px rgba(31, 27, 22, 0.06), 0 1px 2px rgba(31, 27, 22, 0.04)',
      }}
    >
      {/* Thumbnail — diagonal stripes */}
      <div
        style={{
          aspectRatio: '4 / 3',
          backgroundImage: `repeating-linear-gradient(135deg, ${toneA} 0 14px, ${toneB} 14px 28px)`,
        }}
      />
      {/* Text block */}
      <div style={{ padding: '8px 10px 10px' }}>
        <div
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '9px',
            fontWeight: 700,
            letterSpacing: '0.1em',
            color: '#1F6A6A',
          }}
        >
          {eyebrow}
        </div>
        <div
          style={{
            fontFamily: '"DM Serif Display", serif',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: 1.1,
            letterSpacing: '-0.01em',
            color: '#1F1B16',
            marginTop: '2px',
          }}
        >
          {title}
        </div>
      </div>
    </div>
  );
}
